import { useMemo, useState } from "react";
import { Loader2, Sparkles, ChevronRight, X } from "lucide-react";
import { switchToAnnual } from "@/lib/api";
import { toast } from "sonner";
import { track } from "@/lib/analytics";

// Per-plan annual savings (12 * monthly - annual). Kept in sync with
// /app/backend/routes/payment_routes.py:_PLAN_ANNUAL_USD.
const SAVINGS_BY_PLAN = {
    investor_lite: { monthly: 29, annual: 290, savings: 58 },
    investor_pro:  { monthly: 79, annual: 790, savings: 158 },
    investor_plus: { monthly: 149, annual: 1490, savings: 298 },
};

/**
 * Inline CTA shown above the Subscriptions card on /dashboard for
 * users with an active MONTHLY investor subscription. Clicking
 * "Switch & save" fires `POST /api/subscriptions/switch-to-annual`,
 * which calls Stripe with prorated credit (no immediate charge) and
 * flips the local row's `billing_interval` to `annual`.
 *
 * Hidden silently for users on annual / without an active sub.
 */
export default function SwitchToAnnualCard({ subscriptions, onSwitched }) {
    const [busy, setBusy] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    const candidate = useMemo(() => {
        if (!subscriptions?.length) return null;
        return subscriptions.find(
            (s) =>
                s.status === "active"
                && (s.billing_interval || "monthly") === "monthly"
                && SAVINGS_BY_PLAN[s.plan],
        );
    }, [subscriptions]);

    if (!candidate || dismissed) return null;
    const info = SAVINGS_BY_PLAN[candidate.plan];

    const handleSwitch = async () => {
        if (!window.confirm(
            `Switch ${candidate.plan.replace("_", " ")} to annual billing?\n\n`
            + `You'll save $${info.savings}/year. Stripe will apply a prorated `
            + `credit on your next invoice — no immediate charge.`,
        )) return;
        setBusy(true);
        try {
            const r = await switchToAnnual();
            track("subscription_switched_to_annual", {
                plan: candidate.plan,
                savings_usd: r?.annual_savings_usd,
            });
            toast.success(
                `Switched to annual — saving $${r?.annual_savings_usd || info.savings}/year.`,
            );
            onSwitched?.(r);
        } catch (e) {
            const msg = e?.response?.data?.detail
                || "Could not switch to annual. Please try again.";
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    return (
        <section
            className="bl-card p-4 sm:p-5 relative bg-[var(--bl-green-soft)]/40 border-[var(--bl-green)]/30 animate-in fade-in slide-in-from-top-1 duration-500"
            data-testid="switch-to-annual-card"
        >
            <button
                type="button"
                onClick={() => setDismissed(true)}
                className="absolute top-2 right-2 p-1 rounded text-bl-muted hover:text-bl"
                aria-label="Dismiss switch to annual offer"
                data-testid="switch-to-annual-dismiss"
                title="Hide this offer"
            >
                <X className="h-3 w-3" />
            </button>
            <div className="flex items-start gap-2.5 pr-7">
                <Sparkles className="h-4 w-4 text-[var(--bl-green)] mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--bl-green)] font-medium">
                        Save 2 months free
                    </div>
                    <div
                        className="font-display font-semibold text-bl text-sm sm:text-base mt-0.5"
                        data-testid="switch-to-annual-headline"
                    >
                        Switch to annual & save{" "}
                        <span
                            className="text-[var(--bl-green)]"
                            data-testid="switch-to-annual-savings"
                        >
                            ${info.savings}/year
                        </span>
                    </div>
                    <p className="mt-1 text-xs text-bl-muted leading-relaxed">
                        You're on {candidate.plan.replace("_", " ")} monthly
                        (${info.monthly}/mo). Annual billing is ${info.annual}/yr
                        — a prorated credit covers the difference automatically.
                    </p>
                    <button
                        type="button"
                        onClick={handleSwitch}
                        disabled={busy}
                        className="mt-3 inline-flex items-center gap-1 text-xs bl-btn-primary"
                        data-testid="switch-to-annual-cta"
                    >
                        {busy ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                            <ChevronRight className="h-3 w-3" />
                        )}
                        Switch & save
                    </button>
                </div>
            </div>
        </section>
    );
}
